import { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

interface WebSocketHandlers {
  onConversationUpdate?: (data: any) => void;
  onResponseGenerated?: (data: any) => void;
  onEscalationTriggered?: (data: any) => void;
  onDashboardStats?: (data: any) => void;
  onAIAnalyzing?: (data: any) => void;
  onResponseSent?: (data: any) => void;
  onError?: (error: any) => void;
}

interface UseConversationWebSocketReturn {
  isConnected: boolean;
  socket: Socket | null;
  joinConversation: (conversationId: string) => void;
  leaveConversation: (conversationId: string) => void;
  generateResponse: (conversationId: string, context?: any, preferences?: any) => Promise<void>;
  sendResponse: (conversationId: string, responseId: string, customText?: string) => Promise<void>;
  requestEscalation: (conversationId: string, reason: string, urgency: 'low' | 'medium' | 'high' | 'critical') => Promise<void>;
  disconnect: () => void;
  reconnect: () => void;
}

export const useConversationWebSocket = (handlers: WebSocketHandlers): UseConversationWebSocketReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const createConnection = useCallback(() => {
    try {
      const newSocket = io(import.meta.env.VITE_API_URL || 'http://localhost:3001', {
        path: '/api/conversations/socket',
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: maxReconnectAttempts,
        reconnectionDelay: 1000,
        timeout: 20000,
        forceNew: true
      });

      // Connection event handlers
      newSocket.on('connect', () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;

        // Authenticate the connection
        const userId = localStorage.getItem('userId') || 'anonymous';
        const token = localStorage.getItem('authToken') || 'demo-token';
        
        newSocket.emit('authenticate', {
          userId,
          token,
          permissions: ['read_conversations', 'manage_responses', 'escalate']
        });
      });

      newSocket.on('disconnect', (reason) => {
        console.log('WebSocket disconnected:', reason);
        setIsConnected(false);
        
        // Attempt reconnection if not manually disconnected
        if (reason !== 'io client disconnect' && reconnectAttempts.current < maxReconnectAttempts) {
          attemptReconnect();
        }
      });

      newSocket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error);
        setIsConnected(false);
        handlers.onError?.(error);
        
        if (reconnectAttempts.current < maxReconnectAttempts) {
          attemptReconnect();
        }
      });

      // Authentication handlers
      newSocket.on('authenticated', (data) => {
        console.log('WebSocket authenticated:', data);
      });

      newSocket.on('authentication_error', (error) => {
        console.error('WebSocket authentication error:', error);
        handlers.onError?.(error);
      });

      // Conversation event handlers
      newSocket.on('conversation_update', (data) => {
        console.log('Conversation updated:', data);
        handlers.onConversationUpdate?.(data);
      });

      newSocket.on('conversation_joined', (data) => {
        console.log('Joined conversation:', data.conversationId);
      });

      newSocket.on('conversation_context', (data) => {
        console.log('Received conversation context:', data);
      });

      // AI response handlers
      newSocket.on('ai_analyzing', (data) => {
        console.log('AI analyzing:', data);
        handlers.onAIAnalyzing?.(data);
      });

      newSocket.on('response_generated', (data) => {
        console.log('Response generated:', data);
        handlers.onResponseGenerated?.(data);
      });

      newSocket.on('response_sent', (data) => {
        console.log('Response sent:', data);
        handlers.onResponseSent?.(data);
      });

      // Escalation handlers
      newSocket.on('escalation_triggered', (data) => {
        console.log('Escalation triggered:', data);
        handlers.onEscalationTriggered?.(data);
      });

      // Dashboard data handlers
      newSocket.on('dashboard_stats', (data) => {
        console.log('Dashboard stats updated:', data);
        handlers.onDashboardStats?.(data);
      });

      newSocket.on('active_conversations', (data) => {
        console.log('Active conversations updated:', data);
      });

      newSocket.on('ai_performance', (data) => {
        console.log('AI performance updated:', data);
      });

      // Error handlers
      newSocket.on('error', (error) => {
        console.error('WebSocket error:', error);
        handlers.onError?.(error);
      });

      socketRef.current = newSocket;
      setSocket(newSocket);

      return newSocket;
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      handlers.onError?.(error);
      return null;
    }
  }, [handlers]);

  const attemptReconnect = useCallback(() => {
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    reconnectAttempts.current++;
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000); // Exponential backoff, max 30s

    console.log(`Attempting to reconnect (${reconnectAttempts.current}/${maxReconnectAttempts}) in ${delay}ms`);

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    reconnectTimeoutRef.current = setTimeout(() => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      createConnection();
    }, delay);
  }, [createConnection]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setSocket(null);
    setIsConnected(false);
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttempts.current = 0;
    createConnection();
  }, [disconnect, createConnection]);

  // Socket action methods
  const joinConversation = useCallback((conversationId: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('join_conversation', conversationId);
    }
  }, [isConnected]);

  const leaveConversation = useCallback((conversationId: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('leave_conversation', conversationId);
    }
  }, [isConnected]);

  const generateResponse = useCallback(async (
    conversationId: string, 
    context?: any, 
    preferences?: any
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!socketRef.current || !isConnected) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Response generation timeout'));
      }, 30000); // 30 second timeout

      const responseHandler = (data: any) => {
        if (data.conversationId === conversationId) {
          clearTimeout(timeout);
          socketRef.current?.off('response_generated', responseHandler);
          socketRef.current?.off('error', errorHandler);
          resolve();
        }
      };

      const errorHandler = (error: any) => {
        clearTimeout(timeout);
        socketRef.current?.off('response_generated', responseHandler);
        socketRef.current?.off('error', errorHandler);
        reject(error);
      };

      socketRef.current.on('response_generated', responseHandler);
      socketRef.current.on('error', errorHandler);

      socketRef.current.emit('generate_response', {
        conversationId,
        context,
        preferences
      });
    });
  }, [isConnected]);

  const sendResponse = useCallback(async (
    conversationId: string, 
    responseId: string, 
    customText?: string
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!socketRef.current || !isConnected) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Send response timeout'));
      }, 10000); // 10 second timeout

      const responseHandler = (data: any) => {
        if (data.conversationId === conversationId) {
          clearTimeout(timeout);
          socketRef.current?.off('response_sent', responseHandler);
          socketRef.current?.off('error', errorHandler);
          resolve();
        }
      };

      const errorHandler = (error: any) => {
        clearTimeout(timeout);
        socketRef.current?.off('response_sent', responseHandler);
        socketRef.current?.off('error', errorHandler);
        reject(error);
      };

      socketRef.current.on('response_sent', responseHandler);
      socketRef.current.on('error', errorHandler);

      socketRef.current.emit('send_response', {
        conversationId,
        responseId,
        customText
      });
    });
  }, [isConnected]);

  const requestEscalation = useCallback(async (
    conversationId: string, 
    reason: string, 
    urgency: 'low' | 'medium' | 'high' | 'critical'
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!socketRef.current || !isConnected) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Escalation request timeout'));
      }, 10000); // 10 second timeout

      const escalationHandler = (data: any) => {
        if (data.conversationId === conversationId) {
          clearTimeout(timeout);
          socketRef.current?.off('escalation_triggered', escalationHandler);
          socketRef.current?.off('error', errorHandler);
          resolve();
        }
      };

      const errorHandler = (error: any) => {
        clearTimeout(timeout);
        socketRef.current?.off('escalation_triggered', escalationHandler);
        socketRef.current?.off('error', errorHandler);
        reject(error);
      };

      socketRef.current.on('escalation_triggered', escalationHandler);
      socketRef.current.on('error', errorHandler);

      socketRef.current.emit('request_escalation', {
        conversationId,
        reason,
        urgency
      });
    });
  }, [isConnected]);

  // Initialize connection on mount
  useEffect(() => {
    const newSocket = createConnection();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, []); // Empty dependency array to run only on mount

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    socket,
    joinConversation,
    leaveConversation,
    generateResponse,
    sendResponse,
    requestEscalation,
    disconnect,
    reconnect
  };
}; 